<template>
    <div id="stats" class="landscape-stats s-landscape">
        <div class="s-grid">
            <div v-for="team in teams" :key="`s-court-${team.side}`" class="l-court s-court" :class="`s-court--${team.side}`">
                <button
                    type="button"
                    class="s-team-pick"
                    :class="{ 's-team-pick--selected': selection.side === team.side && !selection.playerId }"
                    :style="{ backgroundColor: team.color, color: team.textColor }"
                    @click="$emit('select-team', team.side)"
                >
                    <span class="s-team-pick__name">{{ team.name }}</span>
                    <span class="s-team-pick__hint">team</span>
                </button>
                <div
                    v-for="item in team.court"
                    :key="item.player.id"
                    class="p-player l-player s-player"
                    :class="{ 's-player--selected': selection.playerId === item.player.id }"
                    @click="$emit('player-click', item.player, team.side)"
                >
                    <div class="p-player__jersey l-player__band" :style="{ backgroundColor: team.color, color: team.textColor }">
                        <span class="l-player__number">{{ item.jersey }}</span>
                        <span class="l-player__surname">{{ item.player.surname || item.player.name }}</span>
                        <v-icon v-if="item.mvp" small color="amber" class="l-player__mvp" title="MVP">mdi-crown</v-icon>
                    </div>
                    <div class="l-player__body">
                        <div class="l-stat l-stat--tl l-stat--main">
                            <span class="l-stat__value">{{ item.points }}</span>
                            <span class="l-stat__label">pts</span>
                        </div>
                        <div class="l-stat l-stat--bl">
                            <span class="l-stat__value">{{ item.rebounds }}</span>
                            <span class="l-stat__label">reb</span>
                        </div>
                        <div class="p-player__photo l-player__photo">
                            <v-icon color="grey"> mdi-account </v-icon>
                        </div>
                        <div class="l-stat l-stat--tr">
                            <span class="l-stat__value">{{ item.assists }}</span>
                            <span class="l-stat__label">ast</span>
                        </div>
                        <div class="l-stat l-stat--br">
                            <span class="l-stat__value p-player__fouls" :class="item.foulClass">{{ item.fouls }}</span>
                            <span class="l-stat__label">fouls</span>
                        </div>
                    </div>
                </div>
                <div v-if="!team.court.length" class="p-court-empty">No players on court</div>
            </div>

            <div class="s-center">
                <slot name="clock" />
                <slot name="selection" />
                <div class="s-actions-card s-actions-card--fill">
                    <div class="sd-grid sd-grid--2 s-shots">
                        <v-btn
                            v-for="action in shotActions"
                            :key="action.key"
                            class="sd-stat-btn s-btn s-btn--shot"
                            :class="{ 'sd-stat-btn--missed': action.key === 'missed' }"
                            depressed
                            :disabled="!action.enabled"
                            v-bind="action.key === 'missed' ? { style: { borderColor: actionColor } } : { color: actionColor, dark: !actionLight, light: actionLight }"
                            @click="$emit('action', action.key)"
                        >
                            <v-icon left :color="action.key === 'missed' ? 'red' : undefined">{{ action.icon }}</v-icon>{{ action.label }}
                        </v-btn>
                    </div>
                    <div class="sd-grid sd-grid--4 s-others">
                        <v-btn
                            v-for="action in otherActions"
                            :key="action.key"
                            class="sd-stat-btn sd-stat-btn--other s-btn"
                            depressed
                            :disabled="!action.enabled"
                            :color="actionColor"
                            :dark="!actionLight"
                            :light="actionLight"
                            @click="$emit('action', action.key)"
                        >
                            <v-icon left small>{{ action.icon }}</v-icon>{{ action.label }}
                        </v-btn>
                    </div>
                </div>
                <div class="p-bench-toggles">
                    <stats-bench-toggle
                        v-for="team in teams"
                        :key="`s-bench-toggle-${team.side}`"
                        :team="team"
                        :faces="4"
                        :open="openBenchSide === team.side"
                        @toggle="$emit('toggle-bench', team.side)"
                    />
                </div>
                <div class="s-tools">
                    <v-btn outlined class="s-tool" @click="$emit('open-team-stats')">
                        <v-icon left small>mdi-chart-bar</v-icon>Team stats
                    </v-btn>
                    <v-btn outlined class="s-tool" @click="$emit('open-feed')">
                        <v-icon left small>mdi-format-list-bulleted</v-icon>Feed
                    </v-btn>
                    <v-btn outlined color="red darken-1" class="s-tool" @click="$emit('end-match')">
                        <v-icon left small>mdi-flag-checkered</v-icon>End match
                    </v-btn>
                </div>
            </div>
        </div>

        <v-expand-transition>
            <stats-bench-panel
                v-if="openTeam"
                overlay
                :team="openTeam"
                :selected-player-id="selection.playerId"
                @close="$emit('toggle-bench', openTeam.side)"
                @player-click="$emit('player-click', $event, openTeam.side)"
            />
        </v-expand-transition>
    </div>
</template>

<script>
    import StatsBenchToggle from '@/components/stats/StatsBenchToggle'
    import StatsBenchPanel from '@/components/stats/StatsBenchPanel'

    // "Select first" layout for tablets held sideways: players on both sides, selection + stat buttons in the middle.
    // Presentational: the page passes the team views, the selection and the actions (with enabled flags).
    export default {
        name: 'StatsSelectLandscape',
        components: { StatsBenchToggle, StatsBenchPanel },
        props: {
            teams: {
                type: Array,
                required: true
            },
            selection: {
                type: Object,
                default: () => ({ side: null, playerId: null })
            },
            actions: {
                type: Array,
                default: () => []
            },
            actionColor: {
                type: String,
                default: '#9e9e9e'
            },
            actionLight: {
                type: Boolean,
                default: false
            },
            openBenchSide: {
                type: Number,
                default: null
            }
        },
        computed: {
            openTeam() {
                return this.teams.find(team => team.side === this.openBenchSide) || null
            },
            shotActions() {
                return this.actions.filter(action => action.key === 'made' || action.key === 'missed')
            },
            otherActions() {
                return this.actions.filter(action => action.key !== 'made' && action.key !== 'missed')
            }
        }
    }
</script>
